import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faShop } from "@fortawesome/pro-light-svg-icons"
import { MRT_ColumnDef, MRT_Row } from "material-react-table"
import {
    fetchSupportPrinterQueue,
    SupportPrinterQueueResponse,
    SupportReportsPrinterQueueQuery,
} from "../printerQueueApi"
import { useApiQuery } from "../../../../../core/api/useApiQuery"
import React, { useCallback, useMemo, useState } from "react"
import { DateTime } from "luxon"
import { useDateTime } from "../../../../localization/useDateTime"
import { useAppLocalization } from "../../../../localization/components/AppLocalizationProvider"
import { useApiQueryTable } from "../../../../../core/api/components/ApiQueryTable"
import { LocalizedStrict } from "../../../../localization/components/AppLocalized"
import { StandardChip } from "../../../../../shared/components/badges/StandardChip"
import { PageHeader } from "../../../../../shared/components/pageHeader/PageHeader"
import {
    Breadcrumb,
    BreadcrumbLink,
    StandardBreadcrumbs,
} from "../../../../../shared/components/links/StandardBreadcrumbs"
import { Page } from "../../../../../shared/components/pageHeader/PageHeaderApi"
import { SUPPORT_REPORTS_ROUTE } from "../../../../../constants/routes"
import { MenuItem } from "@mui/material"
import { PrinterQueueTypes } from "../printerQueueTypes"
import { DeviceCell } from "../components/DeviceCell"
import { SelectMerchantAutocomplete } from "../components/SelectMerchantAutocomplete"
import { OrderIdInput } from "../components/OrderIdInput"
import { DetailedPanel } from "../components/DetailPanel"
import { RawReceiptDialog } from "../components/RawReceiptDialog"
import { StandardTableFilter } from "../../../../../shared/components/table/StandardTableFilters"
import { useInitialDateFilters } from "../../../../../shared/components/table/useInitialDateFilters"
import { StandardDateRangeFilter } from "../../../../../shared/components/table/StandardDateRangeFilter"

function transformData(data: SupportPrinterQueueResponse) {
    return data?.printerQueue || []
}

export function PrinterQueueList() {
    const { l10n } = useAppLocalization()
    const dateTimeFactory = useDateTime()

    const [orderId, setOrderId] = useState("")
    const [merchantId, setMerchantId] = useState<number>()
    const { startDate, endDate } = useInitialDateFilters(DateTime.now().minus({ days: 14 }))
    const [dateState, setDateState] = useState<{
        startDate: DateTime
        endDate: DateTime
    }>({
        startDate: startDate,
        endDate: endDate,
    })
    const [showReceiptModal, setShowReceiptModal] = useState(false)
    const [receiptId, setReceiptId] = useState<number | null>()

    const supportPrinterQueue = useApiQuery(fetchSupportPrinterQueue, {
        queryName: SupportReportsPrinterQueueQuery.PRINTER_QUEUE_FOR_SUPPORT,
        dependencies: {
            orderId,
            merchantId,
            startDate: dateState.startDate.toISODate(),
            endDate: dateState.endDate.toISODate(),
        },
        select: transformData,
    })

    const { ApiQueryTable, columnFactory } = useApiQueryTable(supportPrinterQueue)

    const columns = useMemo<MRT_ColumnDef<PrinterQueueTypes>[]>(
        () => [
            columnFactory.idColumn({
                header: l10n.getString("support-printer-queue-table-header-id"),
                accessorKey: "id",
            }),
            columnFactory.datetimeColumn(
                {
                    header: l10n.getString("support-printer-queue-table-header-created"),
                    accessorKey: "created",
                },
                (row) => row?.created,
            ),
            columnFactory.datetimeColumn(
                {
                    header: l10n.getString("support-printer-queue-table-header-print-at"),
                    accessorKey: "printAt",
                },
                (row) => row?.printAt,
            ),
            columnFactory.nameColumn({
                header: l10n.getString("support-printer-queue-table-header-device-name"),
                accessorKey: "device",
                Cell: ({ row }) => <DeviceCell data={row.original} />,
            }),
            columnFactory.typeColumn({
                header: l10n.getString("support-printer-queue-table-header-order-id"),
                accessorKey: "orderId",
            }),
            columnFactory.statusColumn({
                header: l10n.getString("support-printer-queue-table-header-status"),
                accessorKey: "status",
                Cell: ({ row }) => {
                    return row.original.status === "Printed" ? (
                        <LocalizedStrict id="support-printer-queue-table-status-printed">
                            <StandardChip variant="success" placement="table">
                                Printed
                            </StandardChip>
                        </LocalizedStrict>
                    ) : (
                        <LocalizedStrict id="support-printer-queue-table-status-queue">
                            <StandardChip variant="red" placement="table">
                                Queue
                            </StandardChip>
                        </LocalizedStrict>
                    )
                },
                filterSelectOptions: [
                    {
                        value: "printed",
                        text: l10n.getString("support-printer-queue-table-status-printed"),
                    },
                    {
                        value: "queue",
                        text: l10n.getString("support-printer-queue-table-status-queue"),
                    },
                ],
            }),
        ],
        [l10n, dateTimeFactory],
    )

    const renderRowActionMenuItems = useCallback(
        ({ row, closeMenu }: { row: MRT_Row<PrinterQueueTypes>; closeMenu: () => void }) => [
            <MenuItem
                key={`show-receipt-${row.original.id}`}
                onClick={() => {
                    setReceiptId(row.original.id)
                    setShowReceiptModal(true)
                    closeMenu()
                }}
            >
                <LocalizedStrict id="support-printer-queue-table-raw-receipt-action">
                    <span>Raw receipt</span>
                </LocalizedStrict>
            </MenuItem>,
        ],
        [],
    )

    return (
        <>
            <LocalizedStrict id="support-printer-queue-title" attrs={{ title: true }}>
                <PageHeader
                    page={Page.SUPPORT_PRINTER_QUEUE}
                    title="Printer queue"
                    icon={<FontAwesomeIcon icon={faShop} />}
                >
                    <StandardBreadcrumbs>
                        <LocalizedStrict id="support-nav-item-home">
                            <BreadcrumbLink to={SUPPORT_REPORTS_ROUTE}>Home</BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="support-nav-item-printer-queue">
                            <Breadcrumb>Printer queue</Breadcrumb>
                        </LocalizedStrict>
                    </StandardBreadcrumbs>
                </PageHeader>
            </LocalizedStrict>

            <ApiQueryTable
                columns={columns}
                initialState={{ density: "spacious" }}
                renderRowActionMenuItems={renderRowActionMenuItems}
                renderDetailPanel={({ row }) => <DetailedPanel id={row.original.id} />}
                renderTopToolbarCustomActions={() => (
                    <StandardTableFilter variant="left">
                        <StandardDateRangeFilter value={dateState} onChange={setDateState} />
                        <SelectMerchantAutocomplete merchantId={merchantId} setMerchantId={setMerchantId} />
                        <OrderIdInput value={orderId} onChange={setOrderId} />
                    </StandardTableFilter>
                )}
                renderToolbarInternalActions={({ table }) => <StandardTableFilter variant="right" table={table} />}
            />

            {showReceiptModal && (
                <RawReceiptDialog
                    id={receiptId || 0}
                    open={showReceiptModal}
                    onClose={() => setShowReceiptModal(false)}
                />
            )}
        </>
    )
}
